import { type SyntheticEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PRESET_COLORS } from '@/shared/lib/color';
import { Button, ColorPicker } from '@/shared/ui';

import styles from './LabelManager.module.css';

const OPTIONS = PRESET_COLORS.map((value) => ({ value, label: value }));

interface LabelFormProps {
  initial?: { name: string; color: string };
  submitLabel: string;
  pending: boolean;
  onSubmit: (value: { name: string; color: string }) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

/** Name + color (the shared color picker: presets, or any custom color). */
export function LabelForm({
  initial,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
  onDelete,
}: LabelFormProps) {
  const { t } = useTranslation();
  const id = useId();
  const [name, setName] = useState(initial?.name ?? '');
  const [color, setColor] = useState<string>(initial?.color ?? PRESET_COLORS[0]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    onSubmit({ name: name.trim(), color });
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <label className={styles.field} htmlFor={`${id}-name`}>
        {t('labels.name')}
        <input
          id={`${id}-name`}
          className={styles.input}
          value={name}
          maxLength={50}
          autoFocus
          placeholder={t('labels.namePlaceholder')}
          onChange={(e) => {
            setName(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              onCancel();
            }
          }}
        />
      </label>
      <div className={styles.field}>
        {t('labels.color')}
        <ColorPicker
          label={t('labels.color')}
          value={color}
          options={OPTIONS}
          onPick={(next) => {
            if (next !== null) setColor(next);
          }}
        />
      </div>
      <div className={styles.formActions}>
        <Button type="submit" variant="primary" size="sm" disabled={pending}>
          {submitLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        {onDelete &&
          (confirmDelete ? (
            <>
              <Button size="sm" variant="danger" onClick={onDelete} disabled={pending}>
                {t('labels.confirmDelete')}
              </Button>
              <p className={styles.deleteNote}>{t('labels.deleteNote')}</p>
            </>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className={styles.danger}
              onClick={() => {
                setConfirmDelete(true);
              }}
            >
              {t('common.delete')}
            </Button>
          ))}
      </div>
    </form>
  );
}
